import { useState, useEffect } from 'react';
import {
  FileText,
  Download,
  Eye,
  Award,
  TrendingUp,
  Clock,
  CheckCircle,
  Loader2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

interface CVData {
  personalInfo: {
    name: string;
    email: string;
    phone: string;
    birthDate: string;
    nationality: string;
  };
  summary: {
    totalCertificates: number;
    totalECTS: number;
    totalStudyHours: number;
    highestEQFLevel: number;
    eqfLevels: number[];
    dateRange: {
      from: string;
      to: string;
    };
  };
  certificates: Array<{
    certificateNumber: string;
    title: string;
    issueDate: string;
    organization: string;
    association: string;
    ectsCredits: number;
    eqfLevel: number;
    studyHours: number;
    score: number;
    verificationUrl: string;
  }>;
  competencies: Array<{
    name: string;
    count: number;
    level: number;
  }>;
  learningOutcomes: {
    knowledge: string[];
    skills: string[];
    competence: string[];
  };
}

export function CVBuilderPage() {
  const [cvData, setCvData] = useState<CVData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    fetchCVData();
  }, []);

  async function fetchCVData() {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/cv/data', {
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error('Kunne ikke hente CV-data');
      }

      const data = await res.json();

      if (!data.cvData) {
        setError('Du har ingen attester ennå. Få din første attest for å generere CV!');
        setCvData(null);
      } else {
        setCvData(data.cvData);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadPDF() {
    try {
      setDownloading('pdf');

      // Detect mobile/Android - use direct navigation for better compatibility
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        // For mobile: Open in new tab, let browser handle download
        window.open('/api/cv/pdf', '_blank');
      } else {
        // For desktop: Use blob download
        const res = await fetch('/api/cv/pdf', {
          credentials: 'include'
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || 'Kunne ikke generere PDF');
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CV_${cvData?.personalInfo.name.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (err: any) {
      alert('Feil ved nedlasting: ' + err.message);
    } finally {
      setDownloading(null);
    }
  }

  async function handleDownloadHTML() {
    try {
      setDownloading('html');

      // Detect mobile - use direct navigation for better compatibility
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        // For mobile: Open in new tab
        window.open('/api/cv/download', '_blank');
      } else {
        // For desktop: Use blob download
        const res = await fetch('/api/cv/download', {
          credentials: 'include'
        });

        if (!res.ok) {
          throw new Error('Kunne ikke laste ned CV');
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CV_${cvData?.personalInfo.name.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (err: any) {
      alert('Feil ved nedlasting: ' + err.message);
    } finally {
      setDownloading(null);
    }
  }

  async function handlePreview() {
    window.open('/api/cv/html', '_blank');
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="card text-center py-12">
          <Loader2 className="w-12 h-12 text-kf-orange-600 mx-auto mb-4 animate-spin" />
          <p className="text-kf-gray-600">Genererer din CV...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="card text-center py-12">
          <AlertCircle className="w-16 h-16 text-kf-orange-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-kf-gray-900 mb-2">Kunne ikke generere CV</h2>
          <p className="text-kf-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!cvData) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-kf-blue-950">CV-Bygger</h1>
          <p className="text-kf-gray-600 mt-1">
            Generer profesjonell CV basert på dine verifiserte kompetanseattester
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Primary highlight - Orange */}
        <div className="bg-gradient-to-br from-kf-orange-500 to-kf-orange-700 rounded-lg shadow-md p-6 border border-kf-orange-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 text-white">Totalt attester</p>
              <p className="text-3xl font-bold text-white">{cvData.summary.totalCertificates}</p>
            </div>
            <Award className="w-12 h-12 opacity-80 text-white" />
          </div>
        </div>

        {/* Secondary - Dark Blue */}
        <div className="bg-gradient-to-br from-kf-blue-600 to-kf-blue-800 rounded-lg shadow-md p-6 border border-kf-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 text-white">Kompetansepoeng (ECTS)</p>
              <p className="text-3xl font-bold text-white">{cvData.summary.totalECTS}</p>
            </div>
            <TrendingUp className="w-12 h-12 opacity-80 text-white" />
          </div>
        </div>

        {/* Tertiary - Light Info Blue */}
        <div className="bg-gradient-to-br from-kf-info-500 to-kf-info-700 rounded-lg shadow-md p-6 border border-kf-info-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 text-white">Studietimer</p>
              <p className="text-3xl font-bold text-white">{cvData.summary.totalStudyHours}</p>
            </div>
            <Clock className="w-12 h-12 opacity-80 text-white" />
          </div>
        </div>

        {/* Quaternary - Medium Blue */}
        <div className="bg-gradient-to-br from-kf-blue-500 to-kf-blue-700 rounded-lg shadow-md p-6 border border-kf-blue-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 text-white">Høyeste EQF-nivå</p>
              <p className="text-3xl font-bold text-white">{cvData.summary.highestEQFLevel}</p>
            </div>
            <CheckCircle className="w-12 h-12 opacity-80 text-white" />
          </div>
        </div>
      </div>

      {/* Download Actions */}
      <div className="card">
        <h2 className="text-2xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
          <Download className="w-6 h-6 text-kf-orange-600" />
          Last ned din CV
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={handlePreview}
            className="flex items-center justify-center gap-2 py-3 px-4 border-2 border-kf-gray-300 rounded-lg hover:bg-kf-gray-50 transition-colors"
          >
            <Eye className="w-5 h-5" />
            <span className="font-semibold">Forhåndsvis CV</span>
          </button>

          <button
            onClick={handleDownloadPDF}
            disabled={downloading === 'pdf'}
            className="flex items-center justify-center gap-2 py-3 px-4 bg-kf-orange-600 text-white rounded-lg hover:bg-kf-orange-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading === 'pdf' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <FileText className="w-5 h-5" />
            )}
            <span className="font-semibold">Last ned PDF</span>
          </button>

          <button
            onClick={handleDownloadHTML}
            disabled={downloading === 'html'}
            className="flex items-center justify-center gap-2 py-3 px-4 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading === 'html' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            <span className="font-semibold">Last ned HTML</span>
          </button>
        </div>
        <p className="text-sm text-kf-gray-600 mt-4">
          <strong>Tips:</strong> PDF-filen er klar til å sendes til arbeidsgivere. HTML-filen kan åpnes i nettleseren og tilpasses videre.
        </p>
      </div>

      {/* Top Competencies */}
      {cvData.competencies.length > 0 && (
        <div className="card">
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
            <Award className="w-6 h-6 text-kf-orange-600" />
            Dine kompetanser
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cvData.competencies.slice(0, 9).map((comp, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-kf-gray-50 rounded-lg"
              >
                <span className="font-medium text-kf-gray-900">{comp.name}</span>
                <span className="px-2 py-1 bg-kf-orange-600 text-white text-xs font-semibold rounded-full">
                  EQF {comp.level}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Certificates List */}
      <div className="card">
        <h2 className="text-2xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-kf-orange-600" />
          Dine attester
        </h2>
        <div className="space-y-3">
          {cvData.certificates.map((cert, idx) => (
            <div
              key={idx}
              className="p-4 bg-kf-gray-50 rounded-lg flex items-center justify-between"
            >
              <div className="flex-1">
                <h3 className="font-semibold text-kf-gray-900">{cert.title}</h3>
                <p className="text-sm text-kf-gray-600">
                  {cert.organization} • {cert.association}
                </p>
                <p className="text-xs text-kf-gray-500 mt-1">
                  {new Date(cert.issueDate).toLocaleDateString('no-NO')} • {cert.certificateNumber}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-semibold text-kf-gray-900">{cert.ectsCredits} ECTS</p>
                  <p className="text-xs text-kf-gray-500">EQF {cert.eqfLevel}</p>
                </div>
                {cert.score && (
                  <div className="px-3 py-1 bg-kf-success-100 text-kf-success-800 rounded-full">
                    <p className="text-sm font-bold">{cert.score}/100</p>
                  </div>
                )}
                <a
                  href={cert.verificationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-kf-blue-600 hover:bg-kf-blue-50 rounded-lg transition-colors"
                  title="Verifiser attest"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Learning Outcomes Preview */}
      {(cvData.learningOutcomes.knowledge.length > 0 ||
        cvData.learningOutcomes.skills.length > 0 ||
        cvData.learningOutcomes.competence.length > 0) && (
        <div className="card">
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-kf-orange-600" />
            Læringsutbytte (utvalg)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cvData.learningOutcomes.knowledge.length > 0 && (
              <div>
                <h3 className="font-semibold text-kf-gray-900 mb-3 text-kf-blue-800">Kunnskap</h3>
                <ul className="space-y-2">
                  {cvData.learningOutcomes.knowledge.slice(0, 5).map((k, idx) => (
                    <li key={idx} className="text-sm text-kf-gray-700 flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-kf-success-600 mt-0.5 flex-shrink-0" />
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {cvData.learningOutcomes.skills.length > 0 && (
              <div>
                <h3 className="font-semibold text-kf-gray-900 mb-3 text-kf-blue-800">Ferdigheter</h3>
                <ul className="space-y-2">
                  {cvData.learningOutcomes.skills.slice(0, 5).map((s, idx) => (
                    <li key={idx} className="text-sm text-kf-gray-700 flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-kf-blue-600 mt-0.5 flex-shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {cvData.learningOutcomes.competence.length > 0 && (
              <div>
                <h3 className="font-semibold text-kf-gray-900 mb-3 text-kf-blue-800">Generell kompetanse</h3>
                <ul className="space-y-2">
                  {cvData.learningOutcomes.competence.slice(0, 5).map((c, idx) => (
                    <li key={idx} className="text-sm text-kf-gray-700 flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-kf-orange-600 mt-0.5 flex-shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
